import { useEffect } from 'react';
import { useMutationState } from '@tanstack/react-query';

import { isSsoAccessBlocked } from '../utils/ssoAccess';
import { useAuth } from './useAuth';
import { SYNC_TIMEZONE_MUTATION_KEY, useSyncTimezone } from './useProfile';

const deviceTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Stores the device's time zone on the user once per app load when the two differ. */
export const useTimezoneReconciliation = (): void => {
  const { user } = useAuth();
  const sync = useSyncTimezone();

  useEffect(() => {
    if (!user || !sync.isIdle || isSsoAccessBlocked(user)) return;
    const zone = deviceTimeZone();
    if (zone && zone !== user.timezone) {
      sync.mutate(zone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.timezone]);
};

/** True once the stored zone equals the device's, or the silent update has settled either way. */
export const useTimezoneSynced = (): boolean => {
  const { user } = useAuth();
  const settled = useMutationState({
    filters: { mutationKey: SYNC_TIMEZONE_MUTATION_KEY },
    select: (mutation) => mutation.state.status === 'success' || mutation.state.status === 'error',
  });

  if (!user) return false;
  const zone = deviceTimeZone();
  return !zone || user.timezone === zone || settled.some(Boolean);
};
